// KB-PDF-UPLOAD: upload a document to Docs (Docs › Upload): a PDF, Word or text file becomes a
// doc whose body is its text as Markdown, the original kept beside it. The dialog shows "Reading
// the text…" while the API extracts (`pages.upload`), then opens the page. On the page, the
// source bar names the file and its extraction status (done, no text found, failed) with
// Download original, Download .md, and Retry when there is no text yet.
// KB-UPLOAD-2 (founder feedback 2026-10-30): the dialog asks "Add as" (Reference = KB, read only
// for everyone; Procedure = SOP; Page) and "Share with" (more spaces, people and DEs, each with a
// level), takes several files at once (each its own doc), and opens pre-filled when files are
// dropped anywhere on Docs or the Work sidebar (`useFileDrop` + `DropOverlay`).
// UPLOAD-PRIVATE-FIRST (founder 2026-10-30): no "Space" to pick — a doc nobody is added to in
// "Share with" is private (the uploader's personal space); a space it was started from is a
// removable share. The modal's own drop area takes files; the upload only stores them and the
// text is read in the background ("Processing…" on the doc until done).

import { PAGE_UPLOAD_LIMITS } from "@companyos/schema/contracts";
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
  InfoTip,
  Label,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FileTextIcon, FileUpIcon, LockIcon, UploadCloudIcon, XIcon } from "lucide-react";
import { type DragEvent, type FormEvent, useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import {
  type AddAs,
  AddAsPicker,
  addAsOf,
  DOC_LEVEL_HELP,
  type MemberInput,
  MemberSharesField,
  useMemberOptions,
} from "./doc-sharing";
import type { PageDetail } from "./page-dialogs";
import { DOC_LEVELS, type ShareInput, SpaceSharesField, shareTargets } from "./space-shares";
import { enqueueUploads } from "./upload-queue";
import { invalidateWork, spacesQuery } from "./work-queries";

export type UploadStatus = "processing" | "done" | "no_text" | "failed";

export const UPLOAD_STATUS: Record<
  UploadStatus,
  { label: string; variant: "neutral" | "success" | "warning" | "danger" }
> = {
  processing: { label: "Processing…", variant: "neutral" },
  done: { label: "Text extracted", variant: "success" },
  no_text: { label: "No text found", variant: "warning" },
  failed: { label: "Couldn't read the text", variant: "danger" },
};

/**
 * UPLOAD-STUCK-FIX: "Processing n%" — a PDF's pages read of its pages, else the read's steps;
 * 0% until the worker starts, never 100% while still processing.
 */
export function processingLabel(progress?: { done: number; total: number } | null): string {
  const pct = progress ? Math.min(99, Math.floor((progress.done / progress.total) * 100)) : 0;
  return `Processing ${pct}%`;
}

/**
 * UPLOAD-STUCK-FIX: an uploaded doc's state on its own line in the Docs list — "Processing n%"
 * while its text is read, "Processing failed — Retry" with the reason, or the other outcomes.
 */
export function DocUploadChip({
  api,
  co,
  pageId,
  upload,
}: {
  api: Api;
  co: string;
  pageId: string;
  upload: {
    status: UploadStatus;
    error?: string | null | undefined;
    progress?: { done: number; total: number } | null | undefined;
  };
}) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () =>
      api.client.pages.uploadRetry({
        params: { co, id: pageId },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSettled: () => invalidateWork(queryClient, co),
  });
  if (upload.status === "done") return null;
  if (upload.status === "processing" || retry.isPending)
    return (
      <Badge variant="neutral" data-slot="doc-processing">
        {processingLabel(upload.status === "processing" ? upload.progress : undefined)}
      </Badge>
    );
  if (upload.status === "failed")
    return (
      <span className="flex min-w-0 items-center gap-1.5" data-slot="doc-processing-failed">
        <button
          type="button"
          title={upload.error ?? undefined}
          onClick={(e) => {
            // the row is a link to the doc: Retry only retries
            e.preventDefault();
            e.stopPropagation();
            retry.mutate();
          }}
          className="relative z-10 shrink-0 rounded-full border border-danger-text/30 bg-danger-soft px-2 py-0.5 text-xs font-medium text-danger-text hover:bg-danger-soft/70"
        >
          Processing failed — Retry
        </button>
        {upload.error ? (
          <span className="min-w-0 truncate text-xs text-danger-text max-md:hidden">
            {upload.error}
          </span>
        ) : null}
      </span>
    );
  return (
    <Badge variant={UPLOAD_STATUS[upload.status].variant}>
      {UPLOAD_STATUS[upload.status].label}
    </Badge>
  );
}

/** The short type label of an uploaded doc's original. */
export function uploadKind(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("wordprocessingml")) return "DOCX";
  return mime === "text/markdown" ? "MD" : "TXT";
}

export const problem = (error: unknown, fallback: string) =>
  error instanceof ApiError
    ? (error.problem?.detail ?? error.problem?.title ?? fallback)
    : error
      ? fallback
      : null;

export const size = (n: number) =>
  n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

const ACCEPTED = PAGE_UPLOAD_LIMITS.accept.split(",");

/** Why a file cannot be uploaded (null: it can). */
function fileProblem(f: File): string | null {
  const ext = `.${f.name.split(".").pop()?.toLowerCase() ?? ""}`;
  if (!ACCEPTED.includes(ext)) return "Not a PDF, Word or text file";
  if (f.size === 0) return "The file is empty";
  if (f.size > PAGE_UPLOAD_LIMITS.maxBytes) return `Over ${PAGE_UPLOAD_LIMITS.maxMb} MB`;
  return null;
}

/**
 * KB-UPLOAD-2: files dragged over an area. `dragging` is true while files are over it (never for
 * dragged text or rows); the drop hands the files over. Spread `handlers` on the area.
 */
export function useFileDrop(onFiles: (files: File[]) => void) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");
  const handlers = {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    onDragLeave: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth.current = 0;
      setDragging(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length) onFiles(files);
    },
  };
  return { dragging, handlers };
}

/** The drag-over state: a dashed frame over the area with what a drop does. */
export function DropOverlay({ label = "Drop files to add them to Docs" }: { label?: string }) {
  return (
    <div
      data-slot="drop-overlay"
      aria-hidden="true"
      className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary bg-primary-soft/80 text-center text-sm font-medium text-primary backdrop-blur-[1px]"
    >
      <FileUpIcon className="size-6" />
      <span>{label}</span>
      <span className="text-xs font-normal text-muted-foreground">
        PDF, Word or text · each file its own doc
      </span>
    </div>
  );
}

const fileKey = (f: File) => `${f.name}:${f.size}`;

/**
 * "Upload documents" (a modal): the files (a drop area, several at once, each becoming its own
 * doc), "Add as" and "Share with" (spaces, people and DEs). UPLOAD-PRIVATE-FIRST: no space to
 * pick — with nobody in "Share with" the doc is private (only its uploader); the request only
 * stores the file, so the modal closes as soon as the files are up and each doc shows
 * "Processing…" until its text has been read in the background.
 */
export function UploadDocDialog({
  api,
  company,
  open,
  spaceId: initialSpace,
  files: initialFiles,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  open: boolean;
  spaceId?: string | undefined;
  /** Dropped files: the dialog opens with them. */
  files?: readonly File[] | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const spaces = useQuery({ ...spacesQuery(api, company.id), enabled: open });
  const all = spaces.data?.data ?? [];
  // UPLOAD-PRIVATE-FIRST: the doc's home is the uploader's personal space (the API's default when
  // no space is named); the only personal space a person sees is their own.
  const mine = all.find((s) => s.kind === "personal")?.id ?? "";
  const options = useMemberOptions(api, company, open);
  const [files, setFiles] = useState<File[]>([]);
  const [addAs, setAddAs] = useState<AddAs>("kb");
  const [shares, setShares] = useState<ShareInput[]>([]);
  const [members, setMembers] = useState<MemberInput[]>([]);
  // SOP-SELF-UPDATE (2026-10-31): a Procedure starts with owner approval on (the API's default);
  // this turns it off at upload, so the DEs that follow it edit it directly.
  const [selfUpdate, setSelfUpdate] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const ids = { file: useId(), share: useId(), selfUpdate: useId() };
  useEffect(() => {
    if (!open) return;
    setFiles([...(initialFiles ?? [])]);
    setAddAs("kb");
    // Started from a space (its Docs, or a drop on it): that space is a share to keep or remove.
    setShares(initialSpace ? [{ space_id: initialSpace, level: addAsOf("kb").spaceLevel }] : []);
    setMembers([]);
    setSelfUpdate(false);
  }, [open, initialSpace, initialFiles]);
  const choice = addAsOf(addAs);
  const targets = shareTargets(all, mine);
  // A preset space that cannot be shared into (personal, archived) is never sent.
  const spaceShares = shares.filter((s) => targets.some((t) => t.id === s.space_id));
  const isPrivate = spaceShares.length === 0 && members.length === 0;
  const addFiles = (picked: readonly File[]) => {
    setFiles((list) => [
      ...list,
      ...picked.filter((p) => !list.some((f) => f.name === p.name && f.size === p.size)),
    ]);
  };
  const zone = useFileDrop(addFiles);
  // A new "Add as" re-applies its default levels to what is already picked.
  const pickAddAs = (next: AddAs) => {
    const c = addAsOf(next);
    setAddAs(next);
    setShares((list) => list.map((s) => ({ ...s, level: c.spaceLevel })));
    // Everyone stays at its own level (Can view unless picked otherwise).
    setMembers((list) =>
      list.map((m) => (m.principal_kind === "company" ? m : { ...m, level: c.memberLevel })),
    );
  };
  const bad = files.map(fileProblem);

  // UPLOAD-NEVER-BLOCKS: the files go to the background queue and the modal closes at once; the
  // upload tray shows each file's bytes going out, then "Processing…". A single file's doc opens
  // when it is stored, if the person is still where they started.
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!files.length || bad.some(Boolean)) return;
    const fields: Record<string, string> = { kind: addAs };
    if (choice.readOnly) fields.read_only = "true";
    if (spaceShares.length) fields.shares = JSON.stringify(spaceShares);
    const sharedMembers = members;
    const noApproval = addAs === "sop" && selfUpdate;
    const from = window.location.pathname;
    const single = files.length === 1;
    enqueueUploads(
      files.map((file) => ({
        api,
        companyId: company.id,
        slug: company.slug,
        file,
        fields,
        // Codex r1: the doc exists now; a sharing refusal is reported, never re-uploaded.
        ...(sharedMembers.length || noApproval
          ? {
              after: async (page: PageDetail) => {
                // SOP-SELF-UPDATE: a new doc has no playbook links yet, so only the approval
                // changes; a refusal leaves approval on and says so.
                if (noApproval)
                  await api.client.pages
                    .playbookSet({
                      params: { co: company.id, id: page.id },
                      body: {
                        owner_seat_id: null,
                        follower_seat_ids: [],
                        item_template_ids: [],
                        owner_approval: false,
                      },
                    })
                    .catch((e) =>
                      toast.add({
                        title: `${page.title}: owner approval kept on`,
                        description: problem(e, "not changed"),
                        type: "error",
                      }),
                    );
                if (sharedMembers.length)
                  await api.client.pages.members.set({
                    params: { co: company.id, id: page.id },
                    body: { members: sharedMembers },
                  });
              },
            }
          : {}),
        onStored: (page: PageDetail) => {
          void invalidateWork(queryClient, company.id);
          if (single && window.location.pathname === from)
            void navigate({
              to: "/c/$slug/work/p/$pageId",
              params: { slug: company.slug, pageId: page.id },
            });
        },
      })),
    );
    onClose();
  };
  const many = files.length > 1;
  // Files dragged anywhere over the modal stay in it: never the page's drop behind (React
  // events bubble through the portal) and never the browser opening the file.
  const keep = (e: DragEvent) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes("Files")) return;
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent
        className="max-h-[90dvh] overflow-y-auto sm:max-w-xl"
        onDragEnter={keep}
        onDragOver={keep}
        onDragLeave={keep}
        onDrop={keep}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            Upload {many ? `${files.length} documents` : "a document"}
            <InfoTip label="About uploads">
              PDF, Word (.docx) or text, up to {PAGE_UPLOAD_LIMITS.maxMb} MB each; several at once
              make one doc each. Its text becomes a doc in Markdown that people and agents read; the
              original stays downloadable. Scanned PDFs have no text to read (no OCR yet).
            </InfoTip>
          </DialogTitle>
          <DialogDescription>Each file becomes a doc. The original is kept.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-5">
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.file}>Files</FieldLabel>
              <input
                ref={picker}
                id={ids.file}
                type="file"
                multiple
                className="sr-only"
                accept={PAGE_UPLOAD_LIMITS.accept}
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = "";
                }}
              />
              {/* The classic drop area: the whole box opens the picker and takes dropped files. */}
              <button
                type="button"
                data-slot="upload-drop-zone"
                data-dragging={zone.dragging ? "" : undefined}
                onClick={() => picker.current?.click()}
                {...zone.handlers}
                className="flex min-h-36 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-border bg-sunken/40 px-4 py-6 text-center transition-colors hover:border-primary/60 hover:bg-primary-soft/40 focus-visible:border-primary focus-visible:outline-none disabled:cursor-default disabled:opacity-60 data-[dragging]:border-primary data-[dragging]:bg-primary-soft"
              >
                <UploadCloudIcon
                  aria-hidden="true"
                  className={`size-8 ${zone.dragging ? "text-primary" : "text-faint"}`}
                />
                <span className="text-sm font-medium">
                  {zone.dragging ? "Drop to add them" : "Drop files here"}
                </span>
                <span className="text-xs text-muted-foreground">
                  or <span className="font-medium text-primary underline">choose files</span> · PDF,
                  Word or text, up to {PAGE_UPLOAD_LIMITS.maxMb} MB each
                </span>
              </button>
              {files.length ? (
                <ul className="m-0 flex list-none flex-col gap-1 p-0" data-slot="upload-files">
                  {files.map((f, i) => (
                    <li
                      key={fileKey(f)}
                      className="group flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm"
                    >
                      <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
                      <span className="min-w-0 flex-1 truncate">{f.name}</span>
                      {bad[i] ? (
                        <Badge variant="danger">{bad[i]}</Badge>
                      ) : (
                        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                          {size(f.size)}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${f.name}`}
                        title="Remove"
                        onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}
                      >
                        <XIcon aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Field>
            <AddAsPicker value={addAs} onChange={pickAddAs} />
            {addAs === "sop" ? (
              <div data-slot="sop-self-update" className="-mt-2 flex items-center gap-2">
                <Checkbox
                  id={ids.selfUpdate}
                  checked={selfUpdate}
                  onCheckedChange={(v) => setSelfUpdate(v === true)}
                />
                <Label htmlFor={ids.selfUpdate} className="font-normal">
                  DE keeps it current without approval
                </Label>
                <InfoTip label="About owner approval">
                  Off: DEs propose changes and the owner approves them. On: the DEs that follow it
                  edit it directly. You can change it later in the doc's playbook.
                </InfoTip>
              </div>
            ) : null}
            <section
              aria-labelledby={ids.share}
              className="flex flex-col gap-2"
              data-slot="share-with"
            >
              <div className="flex items-center gap-1">
                <h3 id={ids.share} className="m-0 text-sm font-medium">
                  Share with
                </h3>
                <InfoTip label="About Share with">
                  Nobody yet: the doc is private, only you see it. Add spaces, people and DEs, or
                  Everyone in the company, each with a level. {DOC_LEVEL_HELP} A DE gets what its
                  seat is given. You can change it later on the doc.
                </InfoTip>
                {isPrivate ? (
                  <span
                    data-slot="private-hint"
                    className="ml-auto flex items-center gap-1 text-xs text-muted-foreground"
                  >
                    <LockIcon aria-hidden="true" className="size-3.5" />
                    Private: only you
                  </span>
                ) : null}
              </div>
              <SpaceSharesField
                spaces={all}
                homeSpaceId={mine}
                value={shares}
                onChange={setShares}
                what="doc"
                levels={DOC_LEVELS}
                defaultLevel={choice.spaceLevel}
                hideLabel
              />
              <MemberSharesField
                options={options}
                value={members}
                onChange={setMembers}
                defaultLevel={choice.memberLevel}
                everyone={{ companyId: company.id, companyName: company.name }}
              />
            </section>
          </FieldGroup>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!files.length || bad.some(Boolean)}>
              Upload
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// UPLOAD-ORIGINAL: the doc's source-file bar ("Original") lives in doc-original.tsx.
